import { useQuery } from '@tanstack/vue-query'
import { useHttp } from '@/shared/lib'
import { computed, ref, type Ref } from 'vue'

/**
 * Функция загрузки дочерних элементов группы инвентаризационного типа
 * с серверной пагинацией и поиском
 * @param objectId
 * @param typeId
 * @param section
 * @param enabled
 */
export const useObjectTypeGroupElements = (
  objectId: Ref<string>,
  typeId: Ref<number>,
  section: Ref<string>,
  enabled: Ref<boolean>,
) => {
  const http = useHttp()
  const page = ref(1)
  const perPage = ref(10)
  const search = ref('')

  const isSystem = computed(() => (section.value === 'manuals' ? 1 : 0))

  const url = computed(() => {
    const searchParams = new URLSearchParams({
      page: String(page.value),
      per_page: String(perPage.value),
      object_type: String(typeId.value),
      is_system: String(isSystem.value),
    })
    if (search.value) searchParams.set('search', search.value)
    return `/inventory/group/${objectId.value}/object-types?${searchParams}`
  })

  const queryKey = computed(() => [
    'object_type_group_elements',
    objectId.value,
    typeId.value,
    isSystem.value,
    page.value,
    perPage.value,
    search.value,
  ])

  const query = useQuery({
    queryKey,
    queryFn: () => http.get(url.value),
    retry: false,
    enabled,
  })

  const items = computed(() => query.data.value?.data ?? [])
  const total = computed(() => query.data.value?.payload?.total ?? 0)
  const pageCount = computed(() => Math.max(1, Math.ceil(total.value / perPage.value)))

  /**
   * Функция применения поиска — сбрасывает список на первую страницу
   * @param value
   */
  const applySearch = (value: string) => {
    page.value = 1
    search.value = value
  }

  /**
   * Функция смены размера страницы — сбрасывает список на первую страницу
   * @param value
   */
  const setPerPage = (value: number) => {
    page.value = 1
    perPage.value = value
  }

  return {
    page,
    perPage,
    items,
    total,
    pageCount,
    applySearch,
    setPerPage,
    isPending: query.isPending,
    refetch: query.refetch,
  }
}
